import { useState, useMemo, useEffect } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { useDebounce } from "use-debounce";
import { X, Search, Globe, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { locationApi } from "@/lib/api/admin-api";
import { VirtualScroller } from "@/components/common/VirtualScroller";
import type { Location } from "@/types/location";

interface AddLocationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  regionId: number;
  regionName: string;
}

export function AddLocationsDialog({
  open,
  onOpenChange,
  regionId,
  regionName,
}: AddLocationsDialogProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();

  const [addedIds, setAddedIds] = useState<Set<number>>(new Set());
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [debouncedSearch] = useDebounce(search, 300);

  const {
    data: currentPagesData,
    isLoading: currentFirstPageLoading,
    hasNextPage: currentHasNextPage,
    isFetchingNextPage: currentIsFetchingNextPage,
    fetchNextPage: currentFetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["locations", "region-members", regionId],
    queryFn: ({ pageParam }) =>
      locationApi.paginate({ regionId, limit: 20, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.hasNext ? last.meta.page + 1 : undefined),
    enabled: open,
  });

  // Auto-fetch remaining pages so originalIds is always complete
  useEffect(() => {
    if (currentHasNextPage && !currentIsFetchingNextPage) {
      currentFetchNextPage();
    }
  }, [currentHasNextPage, currentIsFetchingNextPage, currentFetchNextPage]);

  const loadingCurrent = currentFirstPageLoading || currentIsFetchingNextPage;

  const currentLocations = useMemo(
    () => currentPagesData?.pages.flatMap((p) => p.data) ?? [],
    [currentPagesData],
  );

  const originalIds = useMemo(
    () => new Set((open ? currentLocations : []).map((l) => l.id)),
    [open, currentLocations],
  );

  const selectedIds = useMemo(() => {
    const ids = new Set(originalIds);
    removedIds.forEach((id) => ids.delete(id));
    addedIds.forEach((id) => ids.add(id));
    return ids;
  }, [originalIds, addedIds, removedIds]);

  const handleClose = () => {
    setAddedIds(new Set());
    setRemovedIds(new Set());
    setSearch("");
    setStatusFilter("all");
    onOpenChange(false);
  };

  const toggle = (id: number) => {
    if (originalIds.has(id)) {
      setRemovedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) { next.delete(id); } else { next.add(id); }
        return next;
      });
    } else {
      setAddedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) { next.delete(id); } else { next.add(id); }
        return next;
      });
    }
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      await Promise.all([
        ...[...addedIds].map((id) => locationApi.update(String(id), { regionId })),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...[...removedIds].map((id) => locationApi.update(String(id), { regionId: null as any })),
      ]);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["regions", regionId] });
      void queryClient.invalidateQueries({ queryKey: ["locations", "region-members", regionId] });
      void queryClient.invalidateQueries({ queryKey: ["locations"] });
      toast.success(t`Locations updated`);
      handleClose();
    },
    onError: () => toast.error(t`Failed to update locations`),
  });

  const hasChanges = addedIds.size > 0 || removedIds.size > 0;

  const isActive =
    statusFilter === "active" ? true : statusFilter === "inactive" ? false : undefined;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        hideClose
        className="max-w-[800px] p-0 gap-0 overflow-hidden rounded-xl border border-(--shell-border)"
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-(--shell-border)">
          <div className="flex flex-col gap-2">
            <DialogTitle className="text-2xl font-medium text-(--color-text-primary)">
              {t`Add locations`}
            </DialogTitle>
            <p className="text-base text-(--color-text-secondary) leading-none">
              {regionName}
            </p>
          </div>
          <DialogClose
            onClick={handleClose}
            className="text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors mt-1"
          >
            <X className="size-6" />
          </DialogClose>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-5 p-5 overflow-y-auto max-h-[calc(90vh-190px)]">
          {/* Currently assigned */}
          {(loadingCurrent || currentLocations.length > 0) && (
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-(--color-text-secondary) uppercase tracking-wide">
                {t`Currently in this region`}
                {!loadingCurrent && ` (${currentLocations.length})`}
              </span>
              {loadingCurrent ? (
                <div className="flex flex-col gap-2">
                  {[...Array(2)].map((_, i) => (
                    <div key={i} className="h-[84px] rounded-lg bg-(--color-surface-muted) animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-2 max-h-[220px] overflow-y-auto pr-1">
                  {currentLocations.map((loc) => (
                    <LocationRow
                      key={loc.id}
                      location={loc}
                      checked={selectedIds.has(loc.id)}
                      currentRegionId={regionId}
                      onToggle={() => toggle(loc.id)}
                    />
                  ))}
                </div>
              )}
              <div className="h-px bg-(--shell-border)" />
            </div>
          )}

          {/* Toolbar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 bg-(--color-surface-content) border border-(--shell-border) rounded-lg shadow-xs px-4 py-3 w-[300px]">
              <Search className="size-5 text-(--color-text-secondary) shrink-0" />
              <input
                type="text"
                placeholder={t`Search...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 text-base text-(--color-text-primary) placeholder:text-(--color-text-secondary) bg-transparent outline-none"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-base text-(--color-text-secondary)">{t`Status:`}</span>
              <select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as typeof statusFilter)
                }
                className="text-base font-medium text-(--color-text-primary) bg-transparent outline-none cursor-pointer"
              >
                <option value="all">{t`All`}</option>
                <option value="active">{t`Active`}</option>
                <option value="inactive">{t`Inactive`}</option>
              </select>
            </div>
          </div>

          {/* All locations */}
          <VirtualScroller<Location>
            queryKey={["locations", "picker", debouncedSearch, statusFilter]}
            queryFn={(page, pageSize) =>
              locationApi.paginate({
                page,
                limit: pageSize,
                search: debouncedSearch || undefined,
                isActive,
              })
            }
            pageSize={20}
            className="flex flex-col gap-2"
            emptyMessage={t`No locations found`}
            renderSkeleton={() => (
              <div className="h-[84px] rounded-lg bg-(--color-surface-muted) animate-pulse" />
            )}
            renderItem={(loc) => (
              <LocationRow
                key={loc.id}
                location={loc}
                checked={selectedIds.has(loc.id)}
                currentRegionId={regionId}
                onToggle={() => toggle(loc.id)}
              />
            )}
          />
        </div>

        {/* Footer */}
        <div className="flex gap-3 items-center justify-end p-5 border-t border-(--shell-border) bg-(--color-surface-content)">
          <Button
            variant="outline"
            className="w-[200px] h-[52px] rounded-[10px] border-(--shell-border) text-base font-medium text-(--color-text-primary)"
            onClick={handleClose}
            disabled={saveMutation.isPending}
          >
            {t`Cancel`}
          </Button>
          <Button
            className="w-[200px] h-[52px] rounded-[10px] bg-(--color-action) hover:bg-(--color-action)/90 text-(--color-action-fg) text-base font-medium disabled:opacity-50"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !hasChanges}
          >
            {saveMutation.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            {t`Save changes`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LocationRow({
  location,
  checked,
  onToggle,
  currentRegionId,
}: {
  location: Location;
  checked: boolean;
  onToggle: () => void;
  currentRegionId?: number;
}) {
  const { t } = useLingui();
  const isInAnotherRegion =
    location.region && location.region.id !== currentRegionId;

  const subtitleParts = [
    location.code,
    location.region?.name,
    location.timezone,
  ].filter(Boolean);

  return (
    <div className="flex items-center gap-3 pl-3 pr-5 py-4 rounded-lg bg-(--color-surface-muted) border border-(--color-border)">
      {/* Icon */}
      <div className="size-[52px] shrink-0 rounded-[10px] border border-(--shell-border) bg-(--color-surface-content) flex items-center justify-center">
        <Globe className="size-5 text-(--color-text-secondary)" />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-base font-medium text-(--color-text-primary) whitespace-nowrap">
            {location.name}
          </span>
          <span
            className={cn(
              "px-2 py-0.5 rounded-full text-sm font-medium whitespace-nowrap",
              location.isActive
                ? "bg-(--color-status-active) text-(--brand-white)"
                : "bg-(--color-surface-accent) text-(--color-text-secondary)",
            )}
          >
            {location.isActive ? t`Active` : t`Inactive`}
          </span>
          {isInAnotherRegion && checked && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-(--color-warning-bg) text-(--color-warning-text) whitespace-nowrap">
              {t`Will be moved`}
            </span>
          )}
        </div>
        <span className="text-sm text-(--color-text-secondary) truncate">
          {subtitleParts.join(" • ")}
        </span>
      </div>

      {/* Checkbox */}
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "size-6 rounded-[6px] shrink-0 flex items-center justify-center border transition-colors",
          checked
            ? "bg-(--color-action) border-(--color-action)"
            : "bg-(--color-surface-content) border-(--color-border) hover:border-(--gray-500)",
        )}
      >
        {checked && (
          <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
            <path
              d="M1 5L4.5 8.5L11 1.5"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
    </div>
  );
}
